import { formatPriceWithAssetDecimals } from '../utils/priceFormatter';

export interface AISymbolData {
  symbol: string;
  price: string;
  lastUpdate: number;
  priceHistory?: Array<{price: number, timestamp: number}>;
  strategy?: string;
  config?: any;
  lastSignalTime?: number;
}

export interface AITradingServiceConfig {
  onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => void;
  onPriceUpdate: (symbol: string, price: string) => void;
}

class AITradingService {
  private activeSymbols: Set<string> = new Set();
  private symbolData: Map<string, AISymbolData> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private startupDelays: Map<string, NodeJS.Timeout> = new Map();
  private config: AITradingServiceConfig | null = null;
  private isRunning = false;

  // Testing mode - set to true for very frequent signals
  private readonly TESTING_MODE = true;
  
  // AI startup delay - 1 minute before trading begins
  private readonly AI_STARTUP_DELAY = 20000; // 60 seconds = 1 minute

  constructor() {
    this.startGlobalPriceMonitoring();
  }

  setConfig(config: AITradingServiceConfig) {
    this.config = config;
  }

  addSymbol(symbol: string, strategy: string = 'supertrend', config: any = {}) {
    this.activeSymbols.add(symbol);    
    // Store strategy and config for this symbol
    this.symbolData.set(symbol, {
      symbol,
      price: '0',
      lastUpdate: 0,
      strategy,
      config
    });
    
    // Start AI trading with 1-minute delay
    this.startSymbolMonitoringWithDelay(symbol);
  }

  removeSymbol(symbol: string) {
    this.activeSymbols.delete(symbol);
    
    // Stop monitoring this symbol
    this.stopSymbolMonitoring(symbol);
    
    // Clear any pending startup delay
    this.clearStartupDelay(symbol);
  }

  updateSymbolPrice(symbol: string, price: string) {
    // Get existing data to preserve priceHistory and other properties
    const existingData = this.symbolData.get(symbol);
    
    this.symbolData.set(symbol, {
      ...existingData, // Preserve existing data including priceHistory
      symbol,
      price,
      lastUpdate: Date.now()
    });

    // Notify about price update
    if (this.config) {
      this.config.onPriceUpdate(symbol, price);
    }
  }

  private globalMonitoringInterval: NodeJS.Timeout | null = null;

  private startGlobalPriceMonitoring() {
    if (this.isRunning) return;
    
    this.isRunning = true;
   
    // Monitor all active symbols every 2 seconds
    this.globalMonitoringInterval = setInterval(() => {
      this.monitorAllSymbols();
    }, 2000);
  }

  private startSymbolMonitoring(symbol: string) {
    // Clear any existing interval for this symbol
    this.stopSymbolMonitoring(symbol);    
    // Start AI signal generation for this symbol
    const intervalTime = this.TESTING_MODE ? 1000 : 2000; // 1 second for testing, 2 seconds for normal
    const interval = setInterval(() => {
      this.generateAISignal(symbol);
    }, intervalTime);

    this.intervals.set(symbol, interval);
  }

  private stopSymbolMonitoring(symbol: string) {
    const interval = this.intervals.get(symbol);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(symbol);
    }
  }

  private startSymbolMonitoringWithDelay(symbol: string) {
    // Clear any existing startup delay for this symbol
    this.clearStartupDelay(symbol);
    
    // console.log(`🤖 AI Trading: Starting ${symbol} with 1-minute initialization delay...`);
    
    // Set up 1-minute delay before starting actual trading
    const startupDelay = setTimeout(() => {
      // console.log(`✅ AI Trading: ${symbol} initialization complete - starting trade execution`);
      this.startSymbolMonitoring(symbol);
      this.startupDelays.delete(symbol);
    }, this.AI_STARTUP_DELAY);
    
    this.startupDelays.set(symbol, startupDelay);
  }

  private clearStartupDelay(symbol: string) {
    const delay = this.startupDelays.get(symbol);
    if (delay) {
      clearTimeout(delay);
      this.startupDelays.delete(symbol);
      // console.log(`⏹️ AI Trading: Startup delay cleared for ${symbol}`);
    }
  }

  private monitorAllSymbols() {
    // This method can be used to fetch real-time prices for all active symbols
    // For now, we'll use the prices that are updated via updateSymbolPrice
    this.activeSymbols.forEach(symbol => {
      const data = this.symbolData.get(symbol);
      if (data && Date.now() - data.lastUpdate < 10000) { // 10 seconds timeout
        // Price is fresh, continue monitoring
        return;
      } else {
        // Price is stale, we might want to fetch new price or mark as inactive
      }
    });
  }

  private generateAISignal(symbol: string) {
    const data = this.symbolData.get(symbol);
    if (!data || !data.price) {
      return;
    }

    const price = parseFloat(data.price);
    const strategy = data.strategy || 'supertrend';
    
    // Calculate signal based on selected strategy
    let signal: 'buy' | 'sell' | null = null;
    
    switch (strategy) {
      case 'supertrend':
        signal = this.calculateSuperTrendSignal(symbol, price);
        break;
      case 'stochastic':
        signal = this.calculateStochasticSignal(symbol, price, data.config);
        break;
      case 'bollingerbands':
        signal = this.calculateBollingerBandsSignal(symbol, price, data.config);
        break;
      case 'rsi':
        signal = this.calculateRSISignal(symbol, price, data.config);
        break;
      default:
        signal = this.calculateSuperTrendSignal(symbol, price);
    }
    
    if (signal && this.config) {
      this.config.onSignal(symbol, signal, data.price);
    }
  }

  private calculateSuperTrendSignal(symbol: string, currentPrice: number): 'buy' | 'sell' | null {
    const data = this.symbolData.get(symbol);
    if (!data) {
      return null;
    }

    // Get or initialize price history for this symbol
    if (!data.priceHistory) {
      data.priceHistory = [];
    }

    // Check minimum timeframe (1 minute = 60000ms)
    const now = Date.now();
    const minTimeframe = 60000; // 1 minute
    if (data.lastSignalTime && (now - data.lastSignalTime) < minTimeframe) {
      return null;
    }

    // Add current price to history
    data.priceHistory.push({
      price: currentPrice,
      timestamp: now
    });

    // Keep only last 50 prices for calculation
    if (data.priceHistory.length > 50) {
      data.priceHistory = data.priceHistory.slice(-50);
    }

    // Need at least 5 prices for SuperTrend calculation
    if (data.priceHistory.length < 5) {
      return null;
    }

    // Calculate ATR (Average True Range)
    const atr = this.calculateATR(data.priceHistory, 3);
    
    if (atr === 0) {
      return null;
    }
    
    // Calculate SuperTrend with direction
    const { superTrend, direction } = this.calculateSuperTrend(data.priceHistory, atr, 1.0);
    
    // Get current and previous direction values
    const currentDirection = direction[direction.length - 1];
    const previousDirection = direction[direction.length - 2];
    
    // Determine signal based on direction change (following Pine Script logic)
    let signal: 'buy' | 'sell' | null = null;
    
    if (currentDirection !== undefined && previousDirection !== undefined) {
      // Buy Signal: Direction changed from bearish (-1) to bullish (1) - ta.change(direction) > 0
      if (previousDirection === -1 && currentDirection === 1) {
        signal = 'buy';
        data.lastSignalTime = now; // Update last signal time
      }
      // Sell Signal: Direction changed from bullish (1) to bearish (-1) - ta.change(direction) < 0
      else if (previousDirection === 1 && currentDirection === -1) {
        signal = 'sell';
        data.lastSignalTime = now; // Update last signal time
      }
    }

    return signal;
  }

  private calculateATR(priceHistory: Array<{price: number, timestamp: number}>, period: number): number {
    if (priceHistory.length < period + 1) return 0;

    let trueRanges: number[] = [];
    
    for (let i = 1; i < priceHistory.length; i++) {
      const current = priceHistory[i].price;
      const previous = priceHistory[i - 1].price;
      
      // For simplicity, we'll use price change as true range
      // In real implementation, you'd use high, low, close prices
      const trueRange = Math.abs(current - previous);
      trueRanges.push(trueRange);
    }

    // Calculate ATR as simple moving average of true ranges
    const recentTrueRanges = trueRanges.slice(-period);
    return recentTrueRanges.reduce((sum, tr) => sum + tr, 0) / recentTrueRanges.length;
  }

  private calculateSuperTrend(priceHistory: Array<{price: number, timestamp: number}>, atr: number, multiplier: number): {superTrend: number[], direction: number[]} {
    const superTrend: number[] = [];
    const direction: number[] = [];
    const upperBand: number[] = [];
    const lowerBand: number[] = [];
    
    for (let i = 0; i < priceHistory.length; i++) {
      const price = priceHistory[i].price;
      
      // Calculate upper and lower bands
      upperBand[i] = price + (atr * multiplier);
      lowerBand[i] = price - (atr * multiplier);
      
      if (i === 0) {
        // First value - use lower band as initial SuperTrend, direction = 1 (bullish)
        superTrend.push(lowerBand[i]);
        direction.push(1);
      } else {
        const previousSuperTrend = superTrend[i - 1];
        const previousDirection = direction[i - 1];
        
        // Calculate current SuperTrend based on previous direction
        let currentSuperTrend: number;
        let currentDirection: number;
        
        if (previousDirection === 1) {
          // Previous trend was bullish
          currentSuperTrend = Math.max(lowerBand[i], previousSuperTrend);
          // Check if price breaks below SuperTrend
          if (price < currentSuperTrend) {
            currentDirection = -1; // Change to bearish
            currentSuperTrend = upperBand[i]; // Use upper band for bearish trend
          } else {
            currentDirection = 1; // Stay bullish
          }
        } else {
          // Previous trend was bearish
          currentSuperTrend = Math.min(upperBand[i], previousSuperTrend);
          // Check if price breaks above SuperTrend
          if (price > currentSuperTrend) {
            currentDirection = 1; // Change to bullish
            currentSuperTrend = lowerBand[i]; // Use lower band for bullish trend
          } else {
            currentDirection = -1; // Stay bearish
          }
        }
        
        superTrend.push(currentSuperTrend);
        direction.push(currentDirection);
      }
    }
    
    return { superTrend, direction };
  }

  private calculateStochasticSignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified Stochastic calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 14) return null;

    const prices = data.priceHistory.slice(-14).map(p => p.price);
    const highest = Math.max(...prices);
    const lowest = Math.min(...prices);
    
    if (highest === lowest) return null;
    
    const k = ((currentPrice - lowest) / (highest - lowest)) * 100;
    const oversoldLevel = config?.oversoldLevel || 20;
    const overboughtLevel = config?.overboughtLevel || 80;
    
    if (k <= oversoldLevel) {
      return 'buy';
    } else if (k >= overboughtLevel) {
      return 'sell';
    }
    
    return null;
  }

  private calculateBollingerBandsSignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified Bollinger Bands calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 20) return null;

    const prices = data.priceHistory.slice(-20).map(p => p.price);
    const sma = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    
    // Calculate standard deviation
    const variance = prices.reduce((sum, price) => sum + Math.pow(price - sma, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    
    const multiplier = config?.multiplier || 2;
    const upperBand = sma + (stdDev * multiplier);
    const lowerBand = sma - (stdDev * multiplier);
    
    if (currentPrice <= lowerBand) {
      return 'buy'; // Price touched lower band - oversold
    } else if (currentPrice >= upperBand) {
      return 'sell'; // Price touched upper band - overbought
    }
    
    return null;
  }

  private calculateRSISignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified RSI calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 14) return null;

    const prices = data.priceHistory.slice(-14).map(p => p.price);
    let gains = 0;
    let losses = 0;
    
    for (let i = 1; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      if (change > 0) {
        gains += change;
      } else {
        losses += Math.abs(change);
      }
    }
    
    const avgGain = gains / 13;
    const avgLoss = losses / 13;
    
    if (avgLoss === 0) return null;
    
    const rs = avgGain / avgLoss;
    const rsi = 100 - (100 / (1 + rs));
    
    const oversoldLevel = config?.oversoldLevel || 30;
    const overboughtLevel = config?.overboughtLevel || 70;
    
    if (rsi <= oversoldLevel) {
      return 'buy';
    } else if (rsi >= overboughtLevel) {
      return 'sell';
    }
    
    return null;
  }

  getActiveSymbols(): string[] {
    return Array.from(this.activeSymbols);
  }

  getSymbolData(symbol: string): AISymbolData | undefined {
    return this.symbolData.get(symbol);
  }

  isSymbolActive(symbol: string): boolean {
    const isActive = this.activeSymbols.has(symbol);
    // console.log('AI Trading Service: isSymbolActive for', symbol, ':', isActive, 'Active symbols:', Array.from(this.activeSymbols));
    return isActive;
  }

  isSymbolInStartupDelay(symbol: string): boolean {
    return this.startupDelays.has(symbol);
  }

  getStartupDelayRemaining(symbol: string): number {
    const delay = this.startupDelays.get(symbol);
    if (!delay) return 0;
    
    // Calculate remaining time (approximate)
    // Note: This is an approximation since we can't get exact remaining time from setTimeout
    return this.AI_STARTUP_DELAY;
  }

  // Clear signal for a symbol (useful after trade execution)
  clearSignal(symbol: string) {
    const data = this.symbolData.get(symbol);
    if (data) {
      // Update the data to clear any cached signal state
      this.symbolData.set(symbol, {
        ...data,
        lastSignalTime: undefined
      });
      
      // Note: We don't call onSignal with null as it expects 'buy' | 'sell'
      // The signal clearing is handled by the context layer
    }
  }

  cleanup() {
    // Clear global monitoring interval
    if (this.globalMonitoringInterval) {
      clearInterval(this.globalMonitoringInterval);
      this.globalMonitoringInterval = null;
    }
    
    // Clear all symbol-specific intervals
    this.intervals.forEach((interval, symbol) => {
      clearInterval(interval);
    });
    this.intervals.clear();
    
    // Clear all startup delays
    this.startupDelays.forEach((delay, symbol) => {
      clearTimeout(delay);
    });
    this.startupDelays.clear();
    
    this.activeSymbols.clear();
    this.symbolData.clear();
    this.isRunning = false;
  }
}

// Export singleton instance
export const aiTradingService = new AITradingService();
